import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sparkles, Plus, TrendingUp } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { RequestCard } from "../components/RequestCard";
import { CategoryFilter } from "../components/CategoryFilter";
import { AdSlotCard, injectAdSlots } from "../components/AdSlotCard";
import { api } from "../lib/api";
import { useI18n } from "../i18n";

export default function BoardPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [offersMap, setOffersMap] = useState({});
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const loadRequests = async () => {
    try {
      const res = await api.get("/requests");
      setRequests(res.data);
      // fetch offer counts in parallel (small dataset)
      const counts = {};
      await Promise.all(
        res.data.map(async (r) => {
          try {
            const o = await api.get(`/requests/${r.id}/offers`);
            counts[r.id] = o.data.length;
          } catch {
            counts[r.id] = 0;
          }
        })
      );
      setOffersMap(counts);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const categoryCounts = useMemo(() => {
    const c = {};
    requests.forEach((r) => {
      c[r.category] = (c[r.category] || 0) + 1;
    });
    return c;
  }, [requests]);

  const filtered = useMemo(() => {
    return requests.filter((r) => {
      if (category !== "all" && r.category !== category) return false;
      if (search) {
        const s = search.toLowerCase();
        if (
          !r.title.toLowerCase().includes(s) &&
          !r.description.toLowerCase().includes(s)
        )
          return false;
      }
      return true;
    });
  }, [requests, category, search]);

  const totalOffers = Object.values(offersMap).reduce((a, b) => a + b, 0);

  return (
    <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-6 sm:py-10 md:py-16">
      {/* Nav pill tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-6 sm:mb-10">
        <button
          data-testid="switch-to-requests"
          className="rounded-full bg-foreground text-background px-4 sm:px-5 py-2 text-sm font-semibold"
        >
          {t("tab_requests")}
        </button>
        <button
          onClick={() => navigate("/products")}
          data-testid="switch-to-products"
          className="rounded-full border border-border bg-white px-4 sm:px-5 py-2 text-sm font-semibold hover:border-foreground/30 transition-colors"
        >
          {t("tab_products")}
        </button>
      </div>

      {/* Hero */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 mb-10 lg:mb-20">
        <div className="lg:col-span-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
            </span>
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
              {t("hero_kicker")}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            {t("hero_line_1")}
            <br />
            <span className="text-muted-foreground">{t("hero_line_2")}</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
            {t("subtagline")}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              data-testid="hero-post-request-btn"
              onClick={() => navigate("/new")}
              size="lg"
              className="rounded-md gap-2 h-12 px-6"
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} />
              {t("nav_post")}
            </Button>
            <div className="flex items-center gap-6 pl-3">
              <div>
                <div className="font-display text-2xl font-bold tabular-nums">
                  {requests.length}
                </div>
                <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
                  {t("stat_requests")}
                </div>
              </div>
              <div>
                <div className="font-display text-2xl font-bold tabular-nums">
                  {totalOffers}
                </div>
                <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
                  {t("stat_offers")}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-4 relative hidden lg:block">
          <div className="relative rounded-md border border-border bg-white p-6 overflow-hidden grain-overlay">
            <div className="flex items-start justify-between mb-6">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
                Example request
              </div>
              <Sparkles className="h-4 w-4 text-accent" />
            </div>
            <div className="font-display text-2xl font-semibold leading-tight mb-2">
              Red pencils
              <br />
              production 80,000
            </div>
            <div className="text-sm text-muted-foreground mb-6">
              Vibrant red finish, HB lead
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
              <div>
                <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
                  Budget
                </div>
                <div className="font-mono-brand text-lg font-bold mt-0.5">$500</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
                  Category
                </div>
                <div className="text-sm font-semibold mt-0.5">Office</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -right-6 h-32 w-32 rounded-full bg-accent/10 blur-2xl"></div>
          </div>
        </div>
      </section>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
        <div className="lg:col-span-3">
          <CategoryFilter
            selected={category}
            onSelect={setCategory}
            counts={categoryCounts}
          />
        </div>

        <div className="lg:col-span-9">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-5 w-5" strokeWidth={2} />
              <h2 className="font-display text-2xl font-semibold tracking-tight">
                {filtered.length}{" "}
                <span className="text-muted-foreground font-normal">
                  {filtered.length === 1 ? "request" : "requests"}
                </span>
              </h2>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("search_placeholder")}
                className="pl-9 rounded-md h-10 bg-white"
              />
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-md border border-border bg-white overflow-hidden"
                >
                  <div className="aspect-[4/3] bg-muted" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-full" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="empty-state"
              className="rounded-md border-2 border-dashed border-border bg-white p-12 md:p-16 text-center"
            >
              <div className="mx-auto h-14 w-14 rounded-full bg-secondary flex items-center justify-center mb-5">
                <Plus className="h-6 w-6 text-muted-foreground" strokeWidth={2} />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">
                {t("empty_title")}
              </h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6 leading-relaxed">
                {t("empty_desc")}
              </p>
              <Button
                data-testid="empty-post-request-btn"
                onClick={() => navigate("/new")}
                className="rounded-md gap-2"
              >
                <Plus className="h-4 w-4" />
                {t("empty_cta")}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {injectAdSlots(filtered, 3).map((slot) =>
                slot.kind === "ad" ? (
                  <AdSlotCard key={slot.key} />
                ) : (
                  <RequestCard
                    key={slot.it.id}
                    req={slot.it}
                    index={slot.index}
                    offerCount={offersMap[slot.it.id] || 0}
                  />
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
